/**
 * chatSocket.js — the app's one Socket.io connection.
 *
 * It lived inside ChatScreen until the Messages badge in Layout needed it too:
 * the server emits `dm_message` and `chat_unread` to each user's own room, and
 * listening there is what lets the badge update the moment a message arrives
 * instead of on a poll. Two modules each holding a socket would mean two
 * connections per open app and two identities to keep in step, so both import
 * this one.
 */
import { io } from 'socket.io-client';
import { Auth } from 'aws-amplify';

// One socket for the whole app, created on first use and kept across screens.
//
// `auth` is a function, not a token: socket.io re-runs it on every reconnect,
// and Auth.currentSession() refreshes an expired Cognito token. With a static
// token, the first reconnect after the hour-long token expired (a phone locked
// over lunch) was rejected, and socket.io does not retry a rejected handshake —
// chat went silent until the page was reloaded.
let _socket = null;
let _socketUserId = null;
export function getSocket(userId) {
  if (_socket && _socketUserId !== userId) {
    // A different account signed in on this device — never reuse the old identity.
    _socket.disconnect(); _socket = null;
  }
  if (_socket) {
    if (!_socket.connected && !_socket.active) _socket.connect();
    return _socket;
  }
  const s = io(process.env.REACT_APP_API_URL || '', {
    auth: cb => {
      Auth.currentSession()
        .then(sess => cb({ token: sess.getIdToken().getJwtToken() }))
        .catch(() => cb({}));
    },
    transports: ['websocket'], reconnection: true,
  });
  // A handshake the server rejected leaves the socket inactive for good. Try
  // again shortly, but only while there is still a session to try with.
  s.on('connect_error', () => {
    if (s.active) return;
    setTimeout(() => {
      if (_socket !== s || s.connected) return;
      Auth.currentSession().then(() => s.connect()).catch(() => {});
    }, 3000);
  });
  _socket = s; _socketUserId = userId;
  return s;
}

// Attach handlers and return a function that removes exactly those handlers.
// `s.off(event)` with no handler removes every listener for the event, which
// is how the list and an open conversation used to tear each other's down.
export function listen(s, handlers) {
  Object.entries(handlers).forEach(([ev, fn]) => s.on(ev, fn));
  return () => Object.entries(handlers).forEach(([ev, fn]) => s.off(ev, fn));
}

// Sign-out closes it. Otherwise the socket stays in the old user's room until
// its token fails a reconnect, still receiving their messages with nobody
// listening — on a shared device, for a person who has left.
export function closeChatSocket() {
  if (_socket) _socket.disconnect();
  _socket = null; _socketUserId = null;
}
